import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useActivePromo } from './PromoBanner';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, Check, Loader2, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../config';


const labels = {
  en: {
    tag: 'Limited Offer',
    title: 'Early Bird',
    subtitle: 'Be among the first 1,000 backers and lock in the lowest price ever.',
    price: '4.99',
    onetime: '/ one-time',
    buy: 'Grab my spot',
    remaining: 'spots remaining',
    features: [
      '1 HexoPDF license',
      '1 device of your choice (Desktop or Mobile)',
      'One-time payment',
      'v1.x updates included',
      'Limited to 1,000 backers',
    ],
  },
  fr: {
    tag: 'Offre Limitée',
    title: 'Early Bird',
    subtitle: 'Soyez parmi les 1 000 premiers contributeurs et verrouillez le prix le plus bas.',
    price: '4,99',
    onetime: '/ unique',
    buy: 'Je réserve ma place',
    remaining: 'places restantes',
    features: [
      '1 licence HexoPDF',
      '1 appareil au choix (Desktop ou Mobile)',
      'Paiement unique',
      'Mises à jour v1.x incluses',
      'Limité à 1 000 contributeurs',
    ],
  },
  es: {
    tag: 'Oferta Limitada',
    title: 'Early Bird',
    subtitle: 'Sé de los primeros 1.000 y asegura el precio más bajo.',
    price: '4,99',
    onetime: '/ único',
    buy: 'Reservar mi plaza',
    remaining: 'plazas restantes',
    features: [
      '1 licencia HexoPDF',
      '1 dispositivo a elección (Desktop o Móvil)',
      'Pago único',
      'Actualizaciones v1.x incluidas',
      'Limitado a 1.000 contribuyentes',
    ],
  },
  de: {
    tag: 'Limitiertes Angebot',
    title: 'Early Bird',
    subtitle: 'Seien Sie unter den ersten 1.000 und sichern Sie sich den niedrigsten Preis.',
    price: '4,99',
    onetime: '/ einmalig',
    buy: 'Platz sichern',
    remaining: 'Plätze verfügbar',
    features: [
      '1 HexoPDF-Lizenz',
      '1 Gerät Ihrer Wahl (Desktop oder Mobile)',
      'Einmalzahlung',
      'Updates v1.x inklusive',
      'Limitiert auf 1.000 Unterstützer',
    ],
  },
  ru: { tag: '\u041e\u0433\u0440\u0430\u043d\u0438\u0447\u0435\u043d\u043d\u043e\u0435 \u043f\u0440\u0435\u0434\u043b\u043e\u0436\u0435\u043d\u0438\u0435', title: 'Early Bird', subtitle: '\u0411\u0443\u0434\u044c\u0442\u0435 \u0441\u0440\u0435\u0434\u0438 \u043f\u0435\u0440\u0432\u044b\u0445 1 000 \u0438 \u0437\u0430\u043a\u0440\u0435\u043f\u0438\u0442\u0435 \u043c\u0438\u043d\u0438\u043c\u0430\u043b\u044c\u043d\u0443\u044e \u0446\u0435\u043d\u0443.', price: '4,99', onetime: '/ \u0440\u0430\u0437\u043e\u0432\u043e', buy: '\u0417\u0430\u043d\u044f\u0442\u044c \u043c\u0435\u0441\u0442\u043e', remaining: '\u043c\u0435\u0441\u0442 \u043e\u0441\u0442\u0430\u043b\u043e\u0441\u044c', features: ['1 \u043b\u0438\u0446\u0435\u043d\u0437\u0438\u044f HexoPDF', '1 \u0443\u0441\u0442\u0440\u043e\u0439\u0441\u0442\u0432\u043e (Desktop \u0438\u043b\u0438 Mobile)', '\u0420\u0430\u0437\u043e\u0432\u044b\u0439 \u043f\u043b\u0430\u0442\u0435\u0436', '\u041e\u0431\u043d\u043e\u0432\u043b\u0435\u043d\u0438\u044f v1.x \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u044b', '\u041e\u0433\u0440\u0430\u043d\u0438\u0447\u0435\u043d\u043e 1 000 \u043c\u0435\u0441\u0442'] },
  ja: { tag: '\u9650\u5b9a\u30aa\u30d5\u30a1\u30fc', title: 'Early Bird', subtitle: '\u5148\u77401,000\u540d\u69d8\u9650\u5b9a\u3067\u6700\u4f4e\u4fa1\u683c\u3092\u78ba\u4fdd\u3002', price: '4.99', onetime: '/ \u4e00\u56de\u6255\u3044', buy: '\u4e88\u7d04\u3059\u308b', remaining: '\u6b8b\u308a', features: ['HexoPDF\u30e9\u30a4\u30bb\u30f3\u30b91\u3064', '1\u30c7\u30d0\u30a4\u30b9 (Desktop\u307e\u305f\u306fMobile)', '\u4e00\u56de\u6255\u3044', 'v1.x\u30a2\u30c3\u30d7\u30c7\u30fc\u30c8\u542b\u3080', '\u5148\u77401,000\u540d\u9650\u5b9a'] },
  zh: { tag: '\u9650\u65f6\u4f18\u60e0', title: 'Early Bird', subtitle: '\u6210\u4e3a\u524d1,000\u540d\u652f\u6301\u8005\uff0c\u9501\u5b9a\u6700\u4f4e\u4ef7\u683c\u3002', price: '4.99', onetime: '/ \u4e00\u6b21\u6027', buy: '\u9884\u7559\u540d\u989d', remaining: '\u5269\u4f59\u540d\u989d', features: ['1\u4e2aHexoPDF\u8bb8\u53ef', '1\u53f0\u8bbe\u5907 (Desktop\u6216Mobile)', '\u4e00\u6b21\u6027\u4ed8\u6b3e', 'v1.x\u66f4\u65b0\u5305\u542b', '\u9650\u52361,000\u540d'] },
  ar: { tag: 'عرض محدود', title: 'Early Bird', subtitle: 'كن من أول 1,000 داعم واحصل على أقل سعر.', price: '4.99', onetime: '/ مرة واحدة', buy: 'احجز مكاني', remaining: 'مكان متبقي', features: ['ترخيص HexoPDF واحد', 'جهاز واحد (Desktop أو Mobile)', 'دفعة واحدة', 'تحديثات v1.x مشمولة', 'محدود بـ 1,000 داعم'] },
  it: { tag: 'Offerta Limitata', title: 'Early Bird', subtitle: 'Sii tra i primi 1.000 e blocca il prezzo più basso.', price: '4,99', onetime: '/ unico', buy: 'Prenota il mio posto', remaining: 'posti rimasti', features: ['1 licenza HexoPDF', '1 dispositivo a scelta (Desktop o Mobile)', 'Pagamento unico', 'Aggiornamenti v1.x inclusi', 'Limitato a 1.000 sostenitori'] },
  pt: { tag: 'Oferta Limitada', title: 'Early Bird', subtitle: 'Esteja entre os primeiros 1.000 e garanta o menor preço.', price: '4,99', onetime: '/ único', buy: 'Reservar meu lugar', remaining: 'vagas restantes', features: ['1 licença HexoPDF', '1 dispositivo à escolha (Desktop ou Mobile)', 'Pagamento único', 'Atualizações v1.x incluídas', 'Limitado a 1.000 apoiadores'] },
  nl: { tag: 'Beperkte Aanbieding', title: 'Early Bird', subtitle: 'Wees bij de eerste 1.000 en vergrendel de laagste prijs.', price: '4,99', onetime: '/ eenmalig', buy: 'Mijn plek reserveren', remaining: 'plekken over', features: ['1 HexoPDF-licentie', '1 apparaat naar keuze (Desktop of Mobiel)', 'Eenmalige betaling', 'Updates v1.x inbegrepen', 'Beperkt tot 1.000 supporters'] },
  pl: { tag: 'Oferta Limitowana', title: 'Early Bird', subtitle: 'Bądź wśród pierwszych 1 000 i zablokuj najniższą cenę.', price: '4,99', onetime: '/ jednorazowo', buy: 'Zarezerwuj miejsce', remaining: 'miejsc pozostało', features: ['1 licencja HexoPDF', '1 urządzenie do wyboru (Desktop lub Mobile)', 'Jednorazowa płatność', 'Aktualizacje v1.x w cenie', 'Ograniczone do 1 000 wspierających'] },
  tr: { tag: 'Sınırlı Teklif', title: 'Early Bird', subtitle: 'İlk 1.000 destekçi arasında olun ve en düşük fiyatı yakalayın.', price: '4,99', onetime: '/ tek seferlik', buy: 'Yerimi ayırt', remaining: 'kalan yer', features: ['1 HexoPDF lisansı', '1 cihaz seçiminiz (Masaüstü veya Mobil)', 'Tek seferlik ödeme', 'v1.x güncellemeleri dahil', 'İlk 1.000 destekçiyle sınırlı'] },
  hi: { tag: 'सीमित ऑफ़र', title: 'Early Bird', subtitle: 'पहले 1,000 समर्थकों में शामिल हों और सबसे कम कीमत लॉक करें।', price: '4.99', onetime: '/ एकमुश्त', buy: 'अपनी जगह सुरक्षित करें', remaining: 'जगहें शेष', features: ['1 HexoPDF लाइसेंस', '1 डिवाइस आपकी पसंद का (डेस्कटॉप या मोबाइल)', 'एकमुश्त भुगतान', 'v1.x अपडेट शामिल', '1,000 समर्थकों तक सीमित'] },
};
const FALLBACK_PRICES = {

// Fallback prices in EUR (always available even if API fails)
  early_bird: { eur: 4.99, usd: 5.49 },
  kickstarter_standard: { eur: 6.99, usd: 7.99 },
  kickstarter_standard_mobile: { eur: 10.99, usd: 12.49 },
  kickstarter_founder: { eur: 19.99, usd: 21.99 },
  kickstarter_founder_mobile: { eur: 23.99, usd: 26.99 },
};

function formatAmount(amount, curr, locale) {
  try {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency', currency: curr.toUpperCase(), minimumFractionDigits: 2,
    }).format(amount);
  } catch { return `${amount} ${curr.toUpperCase()}`; }
}

export const EarlyBirdSection = () => {
  const { lang } = useI18n();
  const { user } = useAuth();
  const { currency, symbol } = useCurrency();
  const navigate = useNavigate();
  const l = labels[lang] || labels.en;
  const [spots, setSpots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const { promo, getDiscountedPrice } = useActivePromo();

  useEffect(() => {
    axios.get(`${API}/early-bird-spots`).then(r => setSpots(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/pricing?currency=${currency}`).then(r => setPricing(r.data)).catch(() => {});
  }, [currency]);

  // Get price: API data first, then fallback
  const getPrice = (pkgId) => {
    if (pricing?.[pkgId]) return pricing[pkgId].amount;
    const fb = FALLBACK_PRICES[pkgId];
    return fb?.[currency] || fb?.eur || 4.99;
  };

  const handleCheckout = async () => {
    if (!user) {
      navigate('/login', { state: { redirect: 'early_bird_checkout', message: 'signin_to_buy' } });
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('hexopdf_token');
      const res = await axios.post(`${API}/checkout/create`, {
        package_id: 'early_bird',
        origin_url: window.location.origin,
        currency,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const spotsPercent = spots ? (spots.claimed / spots.total) * 100 : 0;

  return (
    <section className="relative pb-16 sm:pb-24 bg-background overflow-hidden" data-testid="early-bird-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          {/* Tag */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-medium">
              <Gift className="w-4 h-4" />
              {l.tag}
            </div>
          </div>

          {/* Card */}
          <div className="relative p-6 sm:p-10 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-colors" data-testid="early-bird-card">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left: Info */}
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2" data-testid="early-bird-title">
                  {l.title}
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base mb-6">{l.subtitle}</p>

                <ul className="space-y-2.5 mb-6">
                  {l.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                      <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: Price + CTA */}
              <div className="text-center md:text-right">
                <div className="inline-block p-6 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-6">
                  {(() => {
                    const originalPrice = getPrice('early_bird');
                    const discounted = getDiscountedPrice(originalPrice, 'early_bird');
                    if (discounted !== null && discounted < originalPrice) {
                      return (
                        <>
                          <div className="flex items-center justify-center md:justify-end gap-2 mb-1">
                            <span className="text-muted-foreground text-base sm:text-lg line-through">
                              {formatAmount(originalPrice, currency, lang)}
                            </span>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">
                              -{promo.discount_value}{promo.discount_type === 'percentage' ? '%' : '€'}
                            </span>
                          </div>
                          <span className="font-heading text-5xl sm:text-6xl font-bold text-foreground" data-testid="early-bird-price">
                            {formatAmount(discounted, currency, lang)}
                          </span>
                          <span className="text-muted-foreground text-sm ml-2">{l.onetime}</span>
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="font-heading text-5xl sm:text-6xl font-bold text-foreground" data-testid="early-bird-price">
                          {formatAmount(originalPrice, currency, lang)}
                        </span>
                        <span className="text-muted-foreground text-sm ml-2">{l.onetime}</span>
                      </>
                    );
                  })()}
                </div>

                {/* Spots */}
                {spots && (
                  <div className="mb-6 max-w-xs mx-auto md:ml-auto md:mr-0" data-testid="early-bird-spots">
                    <div className="flex items-center justify-center md:justify-end gap-2 mb-2">
                      <Zap className="w-4 h-4 text-emerald-500" />
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold text-lg" data-testid="early-bird-spots-count">
                        {spots.remaining}/{spots.total}
                      </span>
                      <span className="text-muted-foreground text-sm">{l.remaining}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all duration-1000" style={{ width: `${spotsPercent}%` }} />
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-12 px-10 text-base font-medium shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)] transition-all"
                  data-testid="early-bird-buy-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : l.buy}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
